import { OverridedMixpanel } from 'mixpanel-browser';
import { reloadOnCatch } from '../lazyLoadingUtils';
import { MixpanelEventProps, type MixpanelEvent } from './events';
import { PageType } from '@/types';

const asyncMixpanel = reloadOnCatch(import('mixpanel-browser').then((module) => module.default));

let setInitializedMixpanel: (mixpanel: OverridedMixpanel | PromiseLike<OverridedMixpanel>) => void;

/**
 * A promise that resolves initialized mixpanel.
 */
export const asyncInitializedMixpanel: Promise<OverridedMixpanel> = new Promise((resolve) => {
  setInitializedMixpanel = resolve;
});

/**
 * Initializes mixpanel with the given project token.
 */
export async function mixpanelInit(projectToken: string): Promise<void> {
  const mixpanel = await asyncMixpanel;
  mixpanel.init(projectToken, {
    record_sessions_percent: 100, // Record 100% of sessions for session replay, we can adjust this later is we start exceeding 20,000 monthly sessions
    api_host: 'https://api-eu.mixpanel.com', // route requests to Mixpanel's EU servers
  });
  setInitializedMixpanel(mixpanel);
}

/**
 * Tracks a page view. For revenue dashboard we always add a pageType (for now, this could also be empty in the future if needed)
 * Async wrapper for `mixpanel.track_pageview`.
 * If mixpanel is not initialized yet, it will be delayed until mixpanel is initialized.
 */
export async function mixpanelTrackPageView(pageType: PageType | undefined): Promise<void> {
  const mixpanel = await asyncInitializedMixpanel;
  mixpanel.track_pageview({ page: pageType ?? '' });
}

/**
 * Trigger a mixpanel event.
 * Async wrapper for `mixpanel.track`.
 * If mixpanel is not initialized yet, it will be delayed until mixpanel is initialized.
 */
export async function mixpanelTrack<T extends MixpanelEvent>(
  eventName: T,
  props: MixpanelEventProps<T>
): Promise<void> {
  const scrollDepth = `${window.scrollY} px`;
  const mixpanel = await asyncInitializedMixpanel;

  mixpanel.track(eventName, {
    ...props,
    scrollDepth,
  });
}

/**
 * Tracks time it took for an action to occur. Duration is then recorded in the “Duration” property of an event.
 * Async wrapper for `mixpanel.time_event`.
 * If mixpanel is not initialized yet, it will be delayed until mixpanel is initialized.
 *
 * @see https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#timing-events
 */
export async function mixpanelTimeEvent<T extends MixpanelEvent>(eventName: T): Promise<void> {
  const mixpanel = await asyncInitializedMixpanel;
  mixpanel.time_event(eventName);
}